import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import EstimationStep1 from '@/components/estimation/EstimationStep1';
import EstimationStep2 from '@/components/estimation/EstimationStep2';
import EstimationStep3 from '@/components/estimation/EstimationStep3';
import EstimationStep4 from '@/components/estimation/EstimationStep4';
import { validateStep, buildLeadPayload } from '@/utils/estimationValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Estimation() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    property_type: '',
    city: '',
    bedrooms: '',
    capacity: '',
    address: '',
    postal_code: '',
    city_full: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const progress = (step / 4) * 100;

  const handleValidateAndNext = () => {
    const newErrors = validateStep(step, formData);
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    const newErrors = validateStep(4, formData);
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);

    try {
      const payload = buildLeadPayload(formData);
      await axios.post(`${API}/leads`, payload);

      toast.success('Demande d\'estimation envoyée avec succès !');
      navigate('/estimation/confirmation');
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F3EF] py-12 px-6">
        <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2B2B2B] mb-4" data-testid="estimation-title">
            Estimez le potentiel de votre bien
          </h1>
          <p className="text-[#4A4A4A]">
            Cela prend moins de 60 secondes
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" data-testid="estimation-progress" />
          <p className="text-sm text-[#8B8B8B] mt-2 text-center">
            Étape {step} sur 4
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {step === 1 && (
            <EstimationStep1
              formData={formData}
              errors={errors}
              updateFormData={updateFormData}
              onNext={handleValidateAndNext}
            />
          )}

          {step === 2 && (
            <EstimationStep2
              formData={formData}
              errors={errors}
              updateFormData={updateFormData}
              onNext={handleValidateAndNext}
              onBack={handleBack}
            />
          )}

          {step === 3 && (
            <EstimationStep3
              formData={formData}
              errors={errors}
              updateFormData={updateFormData}
              onNext={handleValidateAndNext}
              onBack={handleBack}
            />
          )}

          {step === 4 && (
            <EstimationStep4
              formData={formData}
              errors={errors}
              updateFormData={updateFormData}
              onSubmit={handleSubmit}
              onBack={handleBack}
              isSubmitting={isSubmitting}
            />
          )}
        </div>

        {/* Reassurance */}
        <div className="text-center mt-8 space-y-4">
          <p className="text-sm text-[#8B8B8B]">
            Une question ? Contactez-nous via le formulaire
          </p>
          <div className="bg-white rounded-lg p-6 max-w-2xl mx-auto border-2 border-[#E5E2DB]">
            <p className="text-sm text-[#4A4A4A]">
              🔒 <strong>Vos données sont sécurisées</strong> — Gérées via <a href="https://www.loomy-crm.com" target="_blank" rel="noopener noreferrer" className="text-[#8A9474] hover:text-[#7A8464] font-medium">Loomy CRM</a>, notre outil professionnel de gestion clients conforme aux normes suisses de protection des données.
            </p>
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
